import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { LOCALES } from "./locales";

export const SUPPORTED = ["en", "pt-br"];
export const FALLBACK = "pt-br";

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "pt-br", label: "Português (Brasil)" },
];

// PT-BR e o idioma padrao do sistema e o fallback de toda traducao. Antes do
// login tentamos o idioma do navegador; nao suportado cai para PT-BR.
export function normalize(lang) {
  const value = (lang || "").trim().toLowerCase().replace("_", "-");
  if (SUPPORTED.includes(value)) return value;
  const base = value.split("-")[0];
  if (base === "pt") return "pt-br";
  if (base === "en") return "en";
  return null;
}

export function detectBrowserLanguage() {
  const candidates =
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
  for (const c of candidates) {
    const n = normalize(c);
    if (n) return n;
  }
  return FALLBACK;
}

// ---------------------------------------------------------------------------
// Persistencia da escolha do visitante (cookie)
//
// SEGURANCA — cookie injection: o valor gravado NUNCA vem do que o usuario
// digitou. So escrevemos depois de o valor passar por `normalize()`, e ainda
// assim exigimos que ele esteja em SUPPORTED: uma allowlist fechada de dois
// literais. E isso que impede um valor como `en; Domain=.evil.tld` de virar
// atributo do cookie — escapar a string nao bastaria, porque `document.cookie`
// interpreta `;` como separador de atributo.
//
// Na leitura vale o mesmo: o conteudo do cookie e dado nao confiavel (o usuario
// pode edita-lo no navegador), entao ele so e aceito se sobreviver ao
// `normalize()`; qualquer outra coisa e descartada em silencio.
// ---------------------------------------------------------------------------
const LANGUAGE_COOKIE = "photoe_lang";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export function readLanguageCookie() {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${LANGUAGE_COOKIE}=`));
  if (!match) return null;
  let raw = match.slice(LANGUAGE_COOKIE.length + 1);
  try {
    raw = decodeURIComponent(raw);
  } catch {
    return null; // cookie adulterado com escape invalido
  }
  return normalize(raw);
}

export function writeLanguageCookie(value) {
  if (typeof document === "undefined") return;
  const lang = normalize(value);
  // Allowlist fechada: nada fora de SUPPORTED chega ao document.cookie.
  if (!lang || !SUPPORTED.includes(lang)) return;

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${LANGUAGE_COOKIE}=${encodeURIComponent(lang)}` +
    `; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
}

/** Cookie do visitante -> idioma do navegador -> PT-BR. */
export function initialLanguage() {
  return readLanguageCookie() || detectBrowserLanguage();
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(initialLanguage);

  // Adota um idioma para a SESSAO, sem persistir — e o caminho do idioma
  // efetivo do usuario autenticado (`/api/auth/me/`). A conta manda na sessao,
  // mas nao sobrescreve a escolha explicita que o visitante gravou no cookie.
  const setLanguage = useCallback((value) => {
    setLang(normalize(value) || FALLBACK);
  }, []);

  // Escolha EXPLICITA do usuario (seletor de idioma): vale para a sessao e
  // fica gravada no cookie.
  const chooseLanguage = useCallback((value) => {
    const next = normalize(value) || FALLBACK;
    setLang(next);
    writeLanguageCookie(next);
  }, []);

  // Logout: volta ao idioma preferido do visitante (cookie) ou ao do navegador.
  const resetLanguage = useCallback(() => {
    setLang(initialLanguage());
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const t = useCallback(
    (key, fallbackText) => {
      const table = LOCALES[lang] || LOCALES[FALLBACK];
      return table[key] ?? LOCALES[FALLBACK][key] ?? fallbackText ?? key;
    },
    [lang]
  );

  // Interpolação simples: t("x.y", "…") com {placeholders}.
  const tf = useCallback(
    (key, params = {}, fallbackText) => {
      const text = t(key, fallbackText);
      return Object.keys(params).reduce(
        (acc, k) => acc.replaceAll(`{${k}}`, params[k]),
        text
      );
    },
    [t]
  );

  return (
    <I18nContext.Provider
      value={{ lang, setLanguage, chooseLanguage, resetLanguage, t, tf }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
